// @ts-nocheck
// #region ==================== IMPORTS ====================
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FilterList, Sort, ViewColumn, ExpandMore, ChevronRight, Add } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import StarIcon from '@mui/icons-material/Star';
import UpdateIcon from '@mui/icons-material/Update';
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Popover,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Typography,
  Divider,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { useDispatch, useSelector } from 'react-redux';
import AtomButton from '@/components/atoms/Button';
import AtomCustomBadge from '@/components/atoms/CustomBadge';
import AtomTreeView, { TreeViewNode } from '@/components/atoms/TreeView';
import OrgActionMenu from '@/components/molecules/ActionMenu';
import { DataTable } from '@/components/organisms/DataMUITable';
import OrgDataTable from '@/components/organisms/DataTable';
import OrgDialog from '@/components/organisms/Dialog';
import segmentStyles from '@/pages/Segments/segments.module.css';
import { AppDispatch } from '@/store';
import { getStudyName, getYearSelected } from '@/store/studies/selectors';
import { setYearSelected } from '@/store/studies/slice';
import { selectStudyYearSegments } from '@/store/study-year/selectors';
import { studyYears } from '@/store/study-year/thunks';
import { studyYearCategories } from '@/store/study-year-categories';
import {
  selectChildCategories,
  selectCategoryCompanies as selectStudyYearCategoryCompanies,
} from '@/store/study-year-categories/selectors';
import AddCategoryDialogContent from './Actions/AddCategoryDialogContent';
import AddChildCategoryDialogContent from './Actions/AddChildCategoryDialogContent';
import { CategoryDialogContent } from './Actions/CategoryDialogContent';
import ConcentrationDialogContent from './Actions/ConcentrationDialogContent';
import HistoricalDataDialogContent from './Actions/HistoricalDataDialogContent';

// #endregion

interface CategoryDialog {
  id: string;
  name: string;
  open: boolean;
  action: string;
}

const Segments: React.FC = () => {
  // #region ==================== DISPATCH ====================
  const dispatch = useDispatch<AppDispatch>();
  // #endregion

  // #region ==================== STATES ====================
  const [page, setPage] = useState(0);
  const [childDataDefaultLoading, setchildDataLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isDialogConcentrationOpen, setIsDialogConcentrationOpen] = useState(false);
  const [isDialogHistoricalDataOpen, setIsDialogHistoricalDataOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryDialog>({ open: false });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const [detailColumnsSelected, setDetailColumnsSelected] = useState<string[]>([
    'company_name',
    'revenue',
    // 'concentration',
    'market_share',
    'yoy_revenue_percentage',
    // 'status',
  ]);
  const [isTextDialogOpen, setIsTextDialogOpen] = useState(false);
  const [isAddSegmentDialogOpen, setIsAddSegmentDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadedChildren, setLoadedChildren] = useState<Record<string, any[]>>({});
  const [showCompaniesDirectly, setShowCompaniesDirectly] = useState(false);
  const [isExpandNodeDialogOpen, setIsExpandNodeDialogOpen] = useState(false);
  const [isAddChildDialogOpen, setIsAddChildDialogOpen] = useState(false);
  const [selectedTreeNode, setSelectedTreeNode] = useState<any>(null);
  const [parentCategoryId, setParentCategoryId] = useState<string>('');

  // #endregion

  // #region ================== SELECTORS ================
  const useStudyName = useSelector(getStudyName);
  const useStudyProfile = useSelector(state => state.studies.studyProfile);
  const useYearSelected = useSelector(getYearSelected);
  const categoryCompanies = useSelector(selectStudyYearCategoryCompanies);
  const childCategories = useSelector(selectChildCategories);
  const useSegments = useSelector(selectStudyYearSegments);
  const useSegmentCategory = childCategories
    ? childCategories.map(cat => ({
        id: cat.study_year_category_id || cat.id,
        category_id: cat.category_id,
        name: cat.category_name || 'Unnamed Category',
        revenue: cat.revenue || 0,
        yoy_revenue: cat.yoy_revenue || 0,
        yoy_revenue_percentage: (cat.yoy_revenue_percentage || 0) * 100,
        segment_name: cat.category_name || 'Unknown Segment',
      }))
    : [];
  // const companySegmentError = useSelector(selectStudyCompanySegmentsError);
  // #endregion

  // #region ==================== VARIABLES ====================
  const segmentMenuItems = [
    {
      label: 'History',
      onClick: row => {
        const selectedCategory = {
          id: row.id,
          name: row.name,
          open: true,
          action: 'history',
        };
        setSelectedCategory(selectedCategory);
      },
    },
    {
      label: 'Financials',
      onClick: row => {
        console.log(row);
        const selectedCategory = {
          id: row.category_id,
          name: row.name,
          open: true,
          action: 'financials',
        };
        setSelectedCategory(selectedCategory);
      },
    },
  ];

  const columns = [
    { id: 'name', label: 'Name', sortable: true },
    {
      id: 'revenue',
      label: useYearSelected?.year?.toString() || 'Current Year', // Ensure it's a string
      sortable: true,
      format: (value: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0), // Handle null/undefined values
    },
    {
      id: 'yoy_revenue',
      label: 'YOY Revenue',
      sortable: true,
      format: (value: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0),
    },
    {
      id: 'yoy_revenue_percentage',
      label: 'YOY %',
      sortable: true,
      format: (value: number) => `${(value || 0).toFixed(2)}%`,
    },
  ];

  const COLUMN_DEFINITIONS = {
    company_name: {
      label: 'Company',
      minWidth: 200,
      sortable: true,
      type: 'text',
    },
    revenue: {
      label: 'Revenue',
      minWidth: 120,
      sortable: true,
      type: 'currency',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(params);
      },
    },
    yoy_revenue: {
      label: 'YoY Revenue',
      minWidth: 120,
      sortable: true,
      type: 'currency',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(params);
      },
    },
    yoy_revenue_percentage: {
      label: 'YoY Revenue %',
      minWidth: 120,
      sortable: true,
      type: 'percentage',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return `${(params * 100).toFixed(2)}%`;
      },
    },
    units_sold: {
      label: 'Units Sold',
      minWidth: 120,
      sortable: true,
      type: 'number',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US').format(params);
      },
    },
    selling_price: {
      label: 'Selling Price',
      minWidth: 120,
      sortable: true,
      type: 'currency',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(params);
      },
    },
    yoy_units: {
      label: 'YoY Units',
      minWidth: 120,
      sortable: true,
      type: 'number',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US').format(params);
      },
    },
    yoy_units_percentage: {
      label: 'YoY Units %',
      minWidth: 120,
      sortable: true,
      type: 'percentage',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return `${(params * 100).toFixed(2)}%`;
      },
    },
    yoy_selling_price: {
      label: 'YoY Selling Price',
      minWidth: 120,
      sortable: true,
      type: 'currency',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(params);
      },
    },
    yoy_selling_price_percent: {
      label: 'YoY Selling Price %',
      minWidth: 120,
      sortable: true,
      type: 'percentage',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return `${(params * 100).toFixed(2)}%`;
      },
    },
    rank_revenue: {
      label: 'Rank',
      minWidth: 80,
      sortable: true,
      type: 'number',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US').format(params);
      },
    },
    yoy_rank_change: {
      label: 'Rank Change',
      minWidth: 120,
      sortable: true,
      type: 'number',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return new Intl.NumberFormat('en-US').format(params);
      },
    },
    concentration: {
      label: 'Concentration',
      minWidth: 120,
      sortable: true,
      type: 'percentage',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return `${(params * 100).toFixed(2)}%`;
      },
    },
    market_share: {
      label: 'Market Share',
      minWidth: 120,
      sortable: true,
      type: 'percentage',
      valueFormatter: params => {
        if (params === null || params === undefined) return '-';
        return `${(params * 100).toFixed(2)}%`;
      },
    },
    outlook: {
      label: 'Outlook',
      minWidth: 200,
      sortable: true,
      type: 'text',
      renderCell: params => (
        <div
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={params.value}
        >
          {params.value || 'No outlook available'}
        </div>
      ),
    },
    competitive_analysis: {
      label: 'Competitive Analysis',
      minWidth: 200,
      sortable: true,
      type: 'text',
      renderCell: params => (
        <div
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={params.value}
        >
          {params.value || 'No competitive analysis available'}
        </div>
      ),
    },
    note: {
      label: 'Note',
      minWidth: 200,
      sortable: true,
      type: 'text',
      renderCell: params => (
        <div
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={params.value}
        >
          {params.value || '-'}
        </div>
      ),
    },
    status: {
      label: 'Status',
      minWidth: 120,
      sortable: true,
      type: 'status',
      renderCell: params => {
        const getStatusIcon = status => {
          switch (status?.toLowerCase()) {
            case 'modelled':
              return <CurrencyExchangeIcon fontSize="small" />;
            case 'revised':
              return <UpdateIcon fontSize="small" />;
            case 'override':
              return <CheckCircleOutlineIcon fontSize="small" />;
            case 'supplier_input':
              return <StarIcon fontSize="small" />;
            default:
              return null;
          }
        };
        return (
          <AtomCustomBadge
            variant={params.value?.toLowerCase() || 'default'}
            label={params.value || 'Unknown'}
            icon={getStatusIcon(params.value)}
            size="md"
          />
        );
      },
    },
    time_updated: {
      label: 'Last Updated',
      minWidth: 160,
      sortable: true,
      type: 'date',
      valueFormatter: params => {
        if (!params) return '-';
        try {
          const date = new Date(params);
          return isNaN(date.getTime()) ? params : date.toLocaleString();
        } catch (error) {
          return params;
        }
      },
    },
    last_edited_by: {
      label: 'Last Edited By',
      minWidth: 150,
      sortable: true,
      type: 'user',
      valueFormatter: params => {
        if (!params) return '-';
        return (
          `${params.first_name || ''} ${params.last_name || ''}`.trim() || params.email || 'Unknown'
        );
      },
    },
  };

  const allSubDataTableColumns = useMemo(() => {
    // Define the order you want columns to appear
    const columnOrder = [
      'company_name',
      'revenue',
      'market_share',
      'concentration',
      'yoy_revenue_percentage',
      'yoy_revenue',
      'units_sold',
      'yoy_units',
      'yoy_units_percentage',
      'selling_price',
      'yoy_selling_price',
      'yoy_selling_price_percent',
      'rank_revenue',
      'yoy_rank_change',
      'status',
      'outlook',
      'competitive_analysis',
      'note',
      'time_updated',
      'last_edited_by',
    ];

    return columnOrder.map(key => ({
      id: key,
      ...COLUMN_DEFINITIONS[key],
    }));
  }, []);

  const filteredSubDataTableColumns = useMemo(() => {
    const essentialColumns = ['company_name'];

    // Filter columns based on selection and always include essential columns
    // DO NOT add action column here since OrgDataTable renderActions prop handles it
    const selectedColumns = allSubDataTableColumns.filter(
      col => detailColumnsSelected.includes(col.id) || essentialColumns.includes(col.id)
    );

    return selectedColumns; // Remove the [...selectedColumns, actionColumn] part
  }, [detailColumnsSelected, allSubDataTableColumns]);

  const menuItems = [
    {
      label: 'Edit Concentration',
      onClick: row => handleEditConcentration(row),
    },
    {
      label: 'See Historical Data',
      onClick: row => handleHistoricalData(row),
    },
    {
      label: 'Edit Text',
      onClick: row => handleEditText(row),
    },
  ];

  const getColumnLabel = (key: string): string => {
    return (
      COLUMN_DEFINITIONS[key]?.label ||
      key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  const getFilterInputType = (columnKey: string) => {
    const columnType = COLUMN_DEFINITIONS[columnKey]?.type || 'text';

    switch (columnType) {
      case 'currency':
      case 'number':
      case 'percentage':
        return 'number';
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  };
  // #endregion

  // #region ================== FUNCTIONS ================

  const segmentOptions = () => {
    return useSegments.map(res => {
      const segmentName = res.category_name || res.name;
      return {
        value: segmentName, // Use name for value to match selectedSegment state
        label: `${segmentName} (${res.number_of_children || 0} categories)`,
      };
    });
  };

  const segmentTreeData = (): TreeViewNode[] => {
    const convertToTreeNode = (item: any, processedIds = new Set()): TreeViewNode => {
      const itemId = item.study_year_category_id || item.id;

      // ADD THIS: Prevent infinite recursion
      if (processedIds.has(itemId)) {
        return {
          id: itemId,
          label: item.category_name || item.name,
          nodeData: item,
          children: [],
        };
      }
      processedIds.add(itemId);

      const segmentName = item.category_name || item.name;

      let childrenNodes = [];
      if (item.children && item.children.length > 0) {
        childrenNodes = item.children.map(child => convertToTreeNode(child, processedIds));
      } else if (loadedChildren[itemId]) {
        childrenNodes = loadedChildren[itemId].map(child => convertToTreeNode(child, processedIds));
      } else if (item.has_children) {
        childrenNodes = [
          {
            id: `${itemId}_placeholder`,
            label: 'Loading...',
            nodeData: { isPlaceholder: true },
            children: [],
          },
        ];
      }

      return {
        id: itemId,
        label:
          item.has_children || (item.children && item.children.length > 0)
            ? `${segmentName} (${item.number_of_children || item.children?.length || 0} categories)`
            : segmentName,
        nodeData: item,
        children: childrenNodes,
      };
    };

    return useSegments.map(res => convertToTreeNode(res));
  };

  const handleAddSegmentDialogOpen = () => {
    setIsAddSegmentDialogOpen(true);
  };

  const handleAddSegmentDialogClose = () => {
    setIsAddSegmentDialogOpen(false);
  };

  const handleDialogClose = () => {
    const selectedCategory = {
      open: false,
      id: '',
      name: '',
      action: '',
    };
    setSelectedCategory(selectedCategory);
  };

  const handleSortMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleFilterChange = (columnId: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [columnId]: value,
    }));
    setPage(0); // Reset to first page when filter changes
  };

  const handleSortSelect = useCallback(
    (columnId: string) => {
      if (typeof columnId !== 'string') return;

      setSortDirection(prev => (sortBy === columnId && prev === 'asc' ? 'desc' : 'asc'));
      setSortBy(columnId);
      handleSortMenuClose();
    },
    [sortBy]
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (sortModel: { field: string; sort: 'asc' | 'desc' | undefined }) => {
    setSortBy(sortModel.field);
    setSortDirection(sortModel.sort || 'asc');
  };

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleDetailColumnToggle = (columnId: string) => {
    setDetailColumnsSelected(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const handleSelectAllDetailColumns = () => {
    const allAvailableColumns = allSubDataTableColumns.map(col => col.id);
    setDetailColumnsSelected([
      'company_name',
      ...allAvailableColumns.filter(id => id !== 'company_name'),
    ]);
  };

  const handleResetDetailColumns = () => {
    setDetailColumnsSelected([
      'company_name',
      'revenue',
      // 'concentration',
      'market_share',
      'yoy_revenue_percentage',
      // 'status',
    ]);
  };

  const handleEditText = (row: any) => {
    setSelectedRow(row);
    setIsTextDialogOpen(true);
  };

  const handleTextDialogClose = () => {
    setIsTextDialogOpen(false);
    setSelectedRow(null);
  };

  const renderDetail = (row: string) => (
    <>
      {childDataDefaultLoading ? (
        <Paper elevation={2} className="w-full">
          <Box style={{ padding: '8px 16px' }}>
            <Skeleton variant="text" width={200} height={32} />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[1, 2, 3, 4].map(col => (
                    <TableCell key={col}>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <TableRow key={index}>
                      {[1, 2, 3, 4].map(col => (
                        <TableCell key={col}>
                          <Skeleton variant="text" width={`${Math.random() * 50 + 50}%`} />
                        </TableCell>
                      ))}
                      <TableCell padding="checkbox">
                        <Skeleton variant="circular" width={24} height={24} />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <>
          <div className={segmentStyles.options}>
            <h3>Company Details</h3>
            {/* UPDATED: Replace the existing div with buttons with this Stack */}
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={handleSortMenuOpen} startIcon={<Sort />}>
                Sort By
              </Button>
              <Button size="small" onClick={handleFilterMenuOpen} startIcon={<FilterList />}>
                Filter
              </Button>
              <Button size="small" onClick={handleColumnMenuOpen} startIcon={<ViewColumn />}>
                Columns
              </Button>
            </Stack>
          </div>

          {/* Sort Menu - keep existing */}
          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={handleSortMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {/* UPDATED: Change subDataTableColumns to filteredSubDataTableColumns */}
            {filteredSubDataTableColumns
              .filter(col => !col.id.includes('action'))
              .map(column => (
                <MenuItem
                  key={column.id}
                  onClick={() => handleSortSelect(column.id)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {column.label}
                  {sortBy === column.id && (
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </IconButton>
                  )}
                </MenuItem>
              ))}
          </Menu>

          {/* Filter Menu - keep existing but update column reference */}
          <Popover
            open={Boolean(filterMenuAnchor)}
            anchorEl={filterMenuAnchor}
            onClose={handleFilterMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{ mt: 1 }}
          >
            <div style={{ padding: '16px', minWidth: '250px' }}>
              {/* UPDATED: Change subDataTableColumns to filteredSubDataTableColumns */}
              {filteredSubDataTableColumns
                .filter(col => !col.id.includes('action'))
                .map(column => (
                  <div key={column.id} style={{ marginBottom: '12px' }}>
                    <div style={{ marginBottom: '8px', fontWeight: 500 }}>{column.label}</div>
                    <input
                      type="text"
                      placeholder={`Filter ${column.label.toLowerCase()}...`}
                      value={filters[column.id] || ''}
                      onChange={e => handleFilterChange(column.id, e.target.value)}
                      className={segmentStyles.filterInput}
                    />
                  </div>
                ))}
            </div>
          </Popover>

          {/* NEW: Add this Column Selection Menu */}
          <Popover
            open={Boolean(columnMenuAnchor)}
            anchorEl={columnMenuAnchor}
            onClose={handleColumnMenuClose} // ← This function IS being used
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{ mt: 1 }}
          >
            <Box sx={{ p: 2, width: 300 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Select Columns
              </Typography>
              <Typography variant="caption" color="text.secondary" mb={2}>
                Choose which columns to display in the company details table
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <FormGroup>
                  {/* Company Name - Always visible, disabled */}
                  <FormControlLabel
                    control={<Checkbox checked={true} disabled={true} size="small" />}
                    label="Company Name (Required)"
                  />

                  {/* Available columns from the sub data table */}
                  {allSubDataTableColumns
                    .filter(col => col.id !== 'company_name')
                    .map(column => (
                      <FormControlLabel
                        key={column.id}
                        control={
                          <Checkbox
                            checked={detailColumnsSelected.includes(column.id)}
                            onChange={() => handleDetailColumnToggle(column.id)} // ← ADD THIS
                            size="small"
                          />
                        }
                        label={getColumnLabel(column.id)}
                      />
                    ))}
                </FormGroup>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {detailColumnsSelected.length} of {allSubDataTableColumns.length} selected
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={handleSelectAllDetailColumns}>
                    {' '}
                    {/* ← ADD THIS */}
                    Select All
                  </Button>
                  <Button size="small" onClick={handleResetDetailColumns}>
                    {' '}
                    {/* ← ADD THIS */}
                    Reset
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Popover>

          {/* UPDATED: Change columns prop from subDataTableColumns to filteredSubDataTableColumns */}
          <OrgDataTable
            columns={filteredSubDataTableColumns}
            data={paginatedData}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={processedData.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            disableColumnMenu={true}
            disableColumnResize={false}
            enableZebraStripes={true}
            renderActions={params => {
              return (
                <OrgActionMenu
                  menuItems={menuItems}
                  rowData={params}
                  rowId={params.id}
                  className={segmentStyles.action}
                />
              );
            }}
          />
        </>
      )}
    </>
  );

  const handleExpandChange = async (rowId: string, isExpanded: boolean, row: any) => {
    setchildDataLoading(true);
    await fetchCategoryCompanies(row.id); // Use row.id instead of row
    setTimeout(() => {
      setchildDataLoading(false);
    }, 800);
  };

  const handleEditConcentration = async row => {
    setSelectedRow(row);
    openConcentrationDialog();
  };

  const openConcentrationDialog = () => {
    setIsDialogConcentrationOpen(true);
  };

  const closeConcentrationDialog = () => {
    setIsDialogConcentrationOpen(false);
    setDialogView('main');
    setSelectedConcentrationIndex(null);
    setNoteText('');
    setSelectedStatus('');
    setConcentrationVersions([]);
  };

  const handleHistoricalData = async row => {
    setSelectedRow(row);
    console.log(row);
    openHistoricalDataDialog();
  };

  const openHistoricalDataDialog = row => {
    setIsDialogHistoricalDataOpen(true);
  };

  const closeHistoricalDataDialog = () => {
    setIsDialogHistoricalDataOpen(false);
  };

  const handleYearChange = async event => {
    const newYearId = event.target.value;
    const selectedYearData = useStudyProfile?.years_available_data?.find(
      yearItem => yearItem.study_year_id === newYearId
    );
    if (selectedYearData) {
      dispatch(setYearSelected(selectedYearData));
    }
  };

  const handleExpandNodeDialogOpen = (node: any) => {
    setSelectedTreeNode(node);
    setIsExpandNodeDialogOpen(true);
  };

  const handleExpandNodeDialogClose = () => {
    setIsExpandNodeDialogOpen(false);
    setSelectedTreeNode(null);
  };

  const handleAddChildDialogOpen = (node: any) => {
    setSelectedTreeNode(node);
    setIsAddChildDialogOpen(true);
  };

  const handleAddChildDialogClose = () => {
    setIsAddChildDialogOpen(false);
    setSelectedTreeNode(null);
  };

  const handleTreeNodeExpand = (nodeId: string) => {
    // Your existing expand logic
    const findNodeById = (nodes: TreeViewNode[], targetId: string): TreeViewNode | null => {
      for (const node of nodes) {
        if (node.id === targetId) return node;
        if (node.children) {
          const found = findNodeById(node.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const node = findNodeById(segmentTreeData(), nodeId);
    if (node && node.nodeData.has_children && !loadedChildren[nodeId]) {
      fetchSegmentCategory(nodeId);
    }
  };

  const handleTreeAddChild = (nodeId: string) => {
    const findNodeById = (nodes: TreeViewNode[], targetId: string): TreeViewNode | null => {
      for (const node of nodes) {
        if (node.id === targetId) return node;
        if (node.children) {
          const found = findNodeById(node.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const node = findNodeById(segmentTreeData(), nodeId);
    if (node) {
      handleAddChildDialogOpen(node.nodeData);
    }
  };
  // #endregion

  // #region ================== API CALLS ================
  const fetchStudiesSegments = async () => {
    setLoading(true);
    try {
      await dispatch(studyYears.getStudyYearSegments(useYearSelected.study_year_id));
    } catch (error) {
      console.error('Error fetching segments:', error);
    }

    setLoading(false);
  };

  const fetchSegmentCategory = async (categoryId: string) => {
    setLoading(true);
    try {
      const result = await dispatch(
        studyYearCategories.getChildCategories({
          studyYearCategoryId: categoryId,
        })
      );

      const loadedChildrenData = result.payload || [];

      setLoadedChildren(prev => ({
        ...prev,
        [categoryId]: loadedChildrenData,
      }));

      setSelectedCategoryId(categoryId);
    } catch (error) {
      console.error('Error fetching child categories:', error);
    }
    setLoading(false);
  };

  const fetchCategoryCompanies = async (categoryId: string) => {
    try {
      await dispatch(
        studyYearCategories.getCategoryCompanies({
          studyYearCategoryId: categoryId,
        })
      );
    } catch (error) {
      console.error('Error fetching category companies:', error);
    }
  };
  // #endregion

  // #region ================== EFFECTS ================
  useEffect(() => {
    fetchStudiesSegments();
  }, [dispatch, useStudyName, useYearSelected]);

  useEffect(() => {
    if (useSegments.length > 0 && !selectedSegment) {
      const firstSegment = useSegments[0];
      if (firstSegment) {
        const segmentName = firstSegment.category_name || firstSegment.name;
        setSelectedSegment(segmentName);
        fetchSegmentCategory(firstSegment.study_year_category_id);
      }
    }
  }, [useSegments, selectedSegment]);

  // useEffect(() => {
  //   if (companySegmentError) {
  //     // Handle the error - maybe show a snackbar or set local error state
  //     setError(companySegmentError);
  //   }
  // }, [companySegmentError]);
  // #endregion

  // #region ================== MEMO ================
  const processedData = useMemo(() => {
    if (typeof sortBy !== 'string') return categoryCompanies || [];

    let result = (categoryCompanies || []).map(res => ({
      id: res.id || res.concentration_id,
      ...res,
      previous_year_revenue: res.previous_year?.revenue ?? 0,
      previous_year_concentration: res.previous_year?.concentration ?? 0,
      company_total_status: res.company_total?.status ?? '',
    }));

    // Apply filters with enhanced type handling
    if (Object.keys(filters).length > 0) {
      result = result.filter(row => {
        return Object.entries(filters).every(([key, value]) => {
          if (!value) return true;

          const cellValue = row[key];
          const filterType = getFilterInputType(key);

          switch (filterType) {
            case 'number':
              // For numbers, support exact match or range
              const numericCellValue = Number(cellValue);
              const numericFilterValue = Number(value);

              if (isNaN(numericFilterValue)) {
                // If not a number, fall back to string search
                return String(cellValue || '')
                  .toLowerCase()
                  .includes(String(value).toLowerCase());
              }

              // Exact number match or close approximation
              return Math.abs(numericCellValue - numericFilterValue) < 0.01;

            case 'date':
              // For dates, convert to comparable format
              if (!cellValue) return false;
              try {
                const cellDate = new Date(cellValue).toISOString().split('T')[0];
                return cellDate.includes(value);
              } catch {
                return String(cellValue || '')
                  .toLowerCase()
                  .includes(String(value).toLowerCase());
              }

            default:
              // Text search (case insensitive)
              return String(cellValue || '')
                .toLowerCase()
                .includes(String(value).toLowerCase());
          }
        });
      });
    }

    // Apply sorting
    if (sortBy) {
      result.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();
        return sortDirection === 'asc'
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      });
    }

    return result;
  }, [categoryCompanies, filters, sortBy, sortDirection]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return processedData.slice(startIndex, startIndex + rowsPerPage);
  }, [processedData, page, rowsPerPage]);
  // #endregion

  return (
    <Box className={segmentStyles.container}>
      <div className={segmentStyles.headContainer}>
        <h1 className={segmentStyles.header}>{useStudyName}</h1>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel id="year-select-label">Year</InputLabel>
          <Select
            labelId="year-select-label"
            id="year-select"
            value={useYearSelected?.study_year_id || ''} // Extract study_year_id from the object
            label="Year"
            onChange={handleYearChange}
          >
            {useStudyProfile?.years_available_data
              ?.slice()
              ?.sort((a, b) => b.year - a.year)
              ?.map(yearItem => (
                <MenuItem key={yearItem.study_year_id} value={yearItem.study_year_id}>
                  {yearItem.year}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>

      <div className={segmentStyles.mainView}>
        <div className={segmentStyles.treeViewContainer}>
          <AtomTreeView
            nodes={segmentTreeData()}
            onNodeSelect={(event, nodeId) => {
              if (!nodeId) return;
              const findNodeById = (
                nodes: TreeViewNode[],
                targetId: string
              ): TreeViewNode | null => {
                for (const node of nodes) {
                  if (node.id === targetId) {
                    return node;
                  }
                  if (node.children && node.children.length > 0) {
                    const found = findNodeById(node.children, targetId);
                    if (found) return found;
                  }
                }
                return null;
              };

              const selectedNode = findNodeById(segmentTreeData(), nodeId);
              if (selectedNode) {
                if (selectedNode.nodeData.isPlaceholder) {
                  return;
                }

                const segmentName =
                  selectedNode.nodeData.category_name || selectedNode.nodeData.name;
                setSelectedSegment(segmentName);

                if (!selectedNode.nodeData.has_children) {
                  setShowCompaniesDirectly(true);
                  fetchCategoryCompanies(selectedNode.nodeData.study_year_category_id);
                } else {
                  setShowCompaniesDirectly(false);
                  fetchSegmentCategory(selectedNode.nodeData.study_year_category_id);
                }
              }
            }}
            showCustomExpandButton={false}
            showExpandFooter={true}
            onNodeExpand={async (nodeId, nodeData) => {
              if (nodeData.has_children && !loadedChildren[nodeId]) {
                setParentCategoryId(nodeData.category_id);
                await fetchSegmentCategory(nodeId);
              }
            }}
            onAddChild={(nodeId, nodeData) => {
              console.log('Add child to:', nodeId, nodeData);
              // Handle your add logic here
            }}
            customExpandFooter={(node, onAddChild) => (
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleAddChildDialogOpen(node.nodeData);
                }}
                style={{
                  background: 'none',
                  border: '1px dashed #093fc3',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  color: '#093fc3',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                + Add New
              </button>
            )}
            maxHeight="600px"
            variant="default"
            showIcons={true}
            defaultExpandedItems={[]}
            className={segmentStyles.treeView}
          />
          <Box sx={{ p: 2, mt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddSegmentDialogOpen}
              sx={{
                borderStyle: 'dashed',
                borderColor: '#093fc3',
                color: '#093fc3',
                '&:hover': {
                  borderColor: '#093fc3',
                  backgroundColor: 'rgba(9, 63, 195, 0.04)',
                },
              }}
            >
              Add New Segment
            </Button>
          </Box>
        </div>

        {/* Data Table */}
        <div style={{ width: '100%', marginLeft: '16px' }}>
          {showCompaniesDirectly ? (
            // Show companies directly
            <div>
              <div className={segmentStyles.options}>
                <h3>{selectedSegment} - Companies</h3>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={handleSortMenuOpen} startIcon={<Sort />}>
                    Sort By
                  </Button>
                  <Button size="small" onClick={handleFilterMenuOpen} startIcon={<FilterList />}>
                    Filter
                  </Button>
                  <Button size="small" onClick={handleColumnMenuOpen} startIcon={<ViewColumn />}>
                    Columns
                  </Button>
                </Stack>
              </div>

              {/* Sort Menu */}
              <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={handleSortMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {filteredSubDataTableColumns
                  .filter(col => !col.id.includes('action'))
                  .map(column => (
                    <MenuItem
                      key={column.id}
                      onClick={() => handleSortSelect(column.id)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      {column.label}
                      {sortBy === column.id && (
                        <IconButton size="small" sx={{ ml: 1 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </IconButton>
                      )}
                    </MenuItem>
                  ))}
              </Menu>

              {/* Filter Menu */}
              <Popover
                open={Boolean(filterMenuAnchor)}
                anchorEl={filterMenuAnchor}
                onClose={handleFilterMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 1 }}
              >
                <div style={{ padding: '16px', minWidth: '250px' }}>
                  {filteredSubDataTableColumns
                    .filter(col => !col.id.includes('action'))
                    .map(column => (
                      <div key={column.id} style={{ marginBottom: '12px' }}>
                        <div style={{ marginBottom: '8px', fontWeight: 500 }}>{column.label}</div>
                        <input
                          type="text"
                          placeholder={`Filter ${column.label.toLowerCase()}...`}
                          value={filters[column.id] || ''}
                          onChange={e => handleFilterChange(column.id, e.target.value)}
                          className={segmentStyles.filterInput}
                        />
                      </div>
                    ))}
                </div>
              </Popover>

              {/* Column Menu */}
              <Popover
                open={Boolean(columnMenuAnchor)}
                anchorEl={columnMenuAnchor}
                onClose={handleColumnMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 1 }}
              >
                <Box sx={{ p: 2, width: 300 }}>
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Select Columns
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mb={2}>
                    Choose which columns to display in the company details table
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox checked={true} disabled={true} size="small" />}
                        label="Company Name (Required)"
                      />
                      {allSubDataTableColumns
                        .filter(col => col.id !== 'company_name')
                        .map(column => (
                          <FormControlLabel
                            key={column.id}
                            control={
                              <Checkbox
                                checked={detailColumnsSelected.includes(column.id)}
                                onChange={() => handleDetailColumnToggle(column.id)}
                                size="small"
                              />
                            }
                            label={getColumnLabel(column.id)}
                          />
                        ))}
                    </FormGroup>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      {detailColumnsSelected.length} of {allSubDataTableColumns.length} selected
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={handleSelectAllDetailColumns}>
                        Select All
                      </Button>
                      <Button size="small" onClick={handleResetDetailColumns}>
                        Reset
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Popover>

              <OrgDataTable
                columns={filteredSubDataTableColumns}
                data={paginatedData}
                page={page}
                rowsPerPage={rowsPerPage}
                totalRows={processedData.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                disableColumnMenu={true}
                disableColumnResize={false}
                enableZebraStripes={true}
                renderActions={params => (
                  <OrgActionMenu
                    menuItems={menuItems}
                    rowData={params}
                    rowId={params.id}
                    className={segmentStyles.action}
                  />
                )}
              />
            </div>
          ) : (
            // Show segments table
            <DataTable
              columns={columns}
              rows={useSegmentCategory}
              collapsible={true}
              renderDetail={renderDetail}
              title={selectedSegment}
              singleExpand={true}
              onExpandChange={handleExpandChange}
              actions={segmentMenuItems}
              toolbar={
                <AtomButton
                  variant="contained"
                  color="primary"
                  onClick={handleAddSegmentDialogOpen}
                  size="small"
                >
                  Add Segment
                </AtomButton>
              }
            />
          )}
        </div>
      </div>

      {/* Add Segment Dialog */}
      <OrgDialog
        open={isAddSegmentDialogOpen}
        onClose={handleAddSegmentDialogClose}
        title="Add New Segment"
        maxWidth="sm"
      >
        <AddCategoryDialogContent
          onClose={handleAddSegmentDialogClose}
          onSuccess={fetchStudiesSegments}
        />
      </OrgDialog>
      {/* Parent Table Actions */}
      <OrgDialog
        open={selectedCategory.open}
        title={selectedCategory.name}
        onClose={handleDialogClose}
        maxWidth="md"
      >
        <CategoryDialogContent
          {...selectedCategory}
          onClose={() => setSegmentDialogOpen(false)}
          segmentName={selectedSegment}
          categoryName={selectedCategory.name}
          year={useYearSelected.year}
        />
      </OrgDialog>
      {/* Historical Data Dialog */}
      <OrgDialog
        open={isDialogHistoricalDataOpen}
        onClose={closeHistoricalDataDialog}
        maxWidth="md"
        title={selectedRow?.company_name || 'Historical Data'}
      >
        <HistoricalDataDialogContent
          selectedRow={selectedRow}
          selectedSegment={selectedSegment}
          studyYearCategoryCompanyId={selectedRow?.study_year_category_company_id || ''}
        />
      </OrgDialog>
      {/* Concentration Dialog */}
      <OrgDialog
        open={isDialogConcentrationOpen}
        onClose={closeConcentrationDialog}
        maxWidth="md"
        title={selectedRow?.company_name || 'Company Segment'}
      >
        <ConcentrationDialogContent
          selectedRow={selectedRow}
          selectedSegment={selectedSegment}
          onClose={closeConcentrationDialog}
          onSuccess={res => {
            console.log(res);
          }}
        />
      </OrgDialog>
      {/* Text Edit Dialog */}
      <OrgDialog
        open={isTextDialogOpen}
        onClose={handleTextDialogClose}
        title={`Edit Text for ${selectedRow?.company_name || 'Segment'}`}
        maxWidth="md"
      >
        <TextField
          fullWidth
          multiline
          rows={4}
          value={selectedRow?.note || ''}
          onChange={e => setNoteText(e.target.value)}
          placeholder="Enter your text here..."
        />
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Analysis"
          value={selectedRow?.analysis || ''}
          onChange={e => setSelectedStatus(e.target.value)}
          sx={{ mt: 2 }}
        ></TextField>
      </OrgDialog>
      {/* Expand Node Dialog */}
      <OrgDialog
        open={isExpandNodeDialogOpen}
        onClose={handleExpandNodeDialogClose}
        maxWidth="md"
        title="Add Category"
      >
        <AddCategoryDialogContent
          selectedNode={selectedTreeNode}
          onClose={handleExpandNodeDialogClose}
          onExpand={handleTreeNodeExpand}
          onAddChild={handleTreeAddChild}
        />
      </OrgDialog>

      {/* Add Child Category Dialog */}
      <OrgDialog
        open={isAddChildDialogOpen}
        onClose={handleAddChildDialogClose}
        maxWidth="sm"
        title="Add Child Category"
      >
        <AddChildCategoryDialogContent
          parentNode={selectedTreeNode}
          parentCategoryId={parentCategoryId}
          onClose={handleAddChildDialogClose}
          onSuccess={parentId => {
            fetchSegmentCategory(parentId);
            fetchStudiesSegments();
          }}
        />
      </OrgDialog>
    </Box>
  );
};

export default Segments;
